import { Fragment, useEffect, useState, useMemo } from "react";
import React from "react";
import { useLocation, useOutletContext, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import DataTable from "../../../components/ui/table/DataTable";
import FilterModal from "../../../components/ui/popup/FilterModal";
import WelcomeCard from "../../../components/layouts/WelcomeCard";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import ModalNew from "../../../components/ui/popup/Modal";
import ConfirmationModal from "../../../components/ui/popup/ConfirmationModal";
import GhostCardContainer from "../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../components/ui/loading/skeletons/GhostDataTable";
import LoadingToRealTransitionWrapper from "../../../components/ui/transitions/LoadingToRealTransitionWraper";
import {
  PopupHeader,
  PopupContent,
  PopupSection,
} from "../../../components/ui/popup/common";
import {
  updateFiling,
  deleteFiling,
  partialUpdateFiling,
} from "../../../api/filings/filingAPI";
import CpaAccepted from "../../../components/ui/templates/popups/filings/CpaAccepted";
import CpaCheckFiling from "../../../components/ui/templates/popups/filings/CpaCheckFiling";
import CpaFailedAccept from "../../../components/ui/templates/popups/filings/CpaFailedAccept";
import { getLanguageContent } from "../../../utils/language/languageSelector";
import { FILTER_SCHEMA } from "../../../utils/schemas/language/components/filterSchema";

function FilingListV2() {
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const filingStatuses = [
    filterContent.status.completed,
    filterContent.status.draft,
    filterContent.status.active,
    filterContent.status.in_progress,
  ];
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.user_role;
  const cpaStatus = user?.account_status;
  const location = useLocation();
  const navigate = useNavigate();

  // Grab Core State and Sync Hooks from Layout Outlet Context
  const { filingList, loading, fetchFilings } = useOutletContext();

  const [filteredFilingList, setFilteredFilingList] = useState([]);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filingTaken, setFilingTaken] = useState(false);

  // Structural States for Sub-modals (Tracked cleanly by ID references)
  const [checkFilingStates, setCheckFilingStates] = useState({});
  const [acceptFilingStates, setAcceptFilingStates] = useState({});
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [softDeleteConfirmationId, setSoftDeleteConfirmationId] =
    useState(null);
  const [isSoftDeleting, setIsSoftDeleting] = useState(false);

  // Modernized Filter State (Holds active filter values)
  const [filters, setFilters] = useState({
    filingYear: "",
    filingType: "",
    status: "",
  });

  // Draft Filter State (Holds values before "Apply" is triggered)
  const [draftFilters, setDraftFilters] = useState({
    filingYear: "",
    filingType: "",
    status: "",
  });

  const isCpaWithInactiveStatus = userRole === "CPA" && cpaStatus !== "AC";

  // ============================================================
  // LEGACY FORMATTING LOGIC (Preserved for Modern Row Generation)
  // ============================================================
  const formatFilingType = (services) => {
    if (!services || !Array.isArray(services) || services.length === 0)
      return "N/A";
    const serviceMap = {
      PTF: "Personal Tax Filing",
      BTF: "Business Tax Filing",
      TPC: "Tax Planning/Consulting",
    };
    return services.map((service) => serviceMap[service] || service).join(", ");
  };

  const getTaxpayerName = (filing) => {
    const details = filing?.tax_situation?.details;
    if (details?.PTF?.background?.firstname) {
      return `${details.PTF.background.firstname} ${
        details.PTF.background.lastname || ""
      }`.trim();
    }
    if (details?.BTF?.background?.businessName) {
      return details.BTF.background.businessName;
    }
    return "N/A";
  };

  const formatStatus = (status) => {
    const statusMap = {
      DRA: "Draft",
      ACT: "Active",
      PRO: "In Progress",
      COM: "Completed",
      CAN: "Cancelled",
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date =
      typeof dateValue === "number"
        ? new Date(dateValue * 1000)
        : new Date(dateValue);
    if (isNaN(date.getTime())) return "N/A";
    return `${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate(),
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const formatFilingYears = (filing) => {
    if (!filing?.tax_situation?.details) return "N/A";
    const yearsSet = new Set();
    const details = filing.tax_situation.details;
    [details.PTF, details.BTF].forEach((type) => {
      const ptfYears = type?.situation?.taxYears;
      if (Array.isArray(ptfYears))
        ptfYears.forEach((year) => yearsSet.add(year));
    });
    return yearsSet.size === 0 ? "N/A" : Array.from(yearsSet).sort().join(", ");
  };

  // ============================================================
  // BACKEND ACTION MUTATORS
  // ============================================================
  const handleCheckFiling = (filingId, event) => {
    if (event) event.preventDefault();
    setCheckFilingStates((prev) => ({ ...prev, [filingId]: !prev[filingId] }));
  };

  const handleAcceptFiling = async (filing, event) => {
    if (event) event.preventDefault();
    try {
      const response = await updateFiling("tax/filing", {}, filing.filing_uuid);
      const acceptState = {};
      if (response.status === 200) {
        acceptState.accepted = true;
        acceptState.response = 200;
        setAcceptFilingStates((prev) => ({
          ...prev,
          [filing.id]: acceptState,
        }));
        setCheckFilingStates((prev) => ({ ...prev, [filing.id]: false }));
        if (fetchFilings) fetchFilings();
        navigate(`/tax-filing/filing/${filing.filing_uuid}?accepted=true`);
      } else if (response.status === 401) {
        acceptState.accepted = true;
        acceptState.response = 401;
        setAcceptFilingStates((prev) => ({
          ...prev,
          [filing.id]: acceptState,
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteFiling = async () => {
    if (!deleteConfirmationId) return;
    setIsDeleting(true);
    try {
      const response = await deleteFiling("tax/filing", deleteConfirmationId);
      if (response.status === 200 || response.status === 204) {
        if (fetchFilings) fetchFilings();
        setDeleteConfirmationId(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSoftDeleteFiling = async () => {
    if (!softDeleteConfirmationId) return;
    setIsSoftDeleting(true);
    try {
      const response = await partialUpdateFiling(
        "tax/filing",
        { soft_delete: true },
        softDeleteConfirmationId,
      );
      if (response.status === 200) {
        if (fetchFilings) fetchFilings();
        setSoftDeleteConfirmationId(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSoftDeleting(false);
    }
  };

  // ============================================================
  // CPA DIRECT LINK INTERCEPTOR
  // ============================================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fId = params.get("f_id");
    if (fId && userRole === "CPA" && !loading) {
      const isTaken = !filingList.some((item) => item.filing_uuid === fId);
      setFilingTaken(isTaken);
    }
  }, [location.search, filingList, userRole, loading]);

  // ============================================================
  // SEGMENTATION & DROPDOWN FILTER PIPELINE
  // ============================================================
  useEffect(() => {
    let filtered = [...filingList];

    if (location.search.includes("segment=cancelled")) {
      filtered = filtered.filter((filing) => filing.status === "CAN");
    } else if (userRole === "CPA") {
      if (location.search.includes("segment=jobs")) {
        filtered = filtered.filter((filing) => filing.status === "ACT");
      } else {
        filtered = filtered.filter(
          (filing) => filing.status !== "ACT" && filing.status !== "CAN",
        );
      }
    } else {
      filtered = filtered.filter((filing) => filing.status !== "CAN");
    }

    if (filters.filingYear) {
      filtered = filtered.filter((filing) => {
        const yearsSet = new Set();
        const details = filing?.tax_situation?.details;
        [details?.PTF, details?.BTF].forEach((type) => {
          const yrs = type?.situation?.taxYears;
          if (Array.isArray(yrs)) yrs.forEach((y) => yearsSet.add(String(y)));
        });
        return yearsSet.has(filters.filingYear);
      });
    }

    if (filters.filingType) {
      filtered = filtered.filter((f) =>
        formatFilingType(f?.tax_service?.services)
          .toLowerCase()
          .includes(filters.filingType.toLowerCase()),
      );
    }

    if (filters.status) {
      filtered = filtered.filter(
        (f) =>
          formatStatus(f.status).toLowerCase() === filters.status.toLowerCase(),
      );
    }

    setFilteredFilingList(filtered);
  }, [filingList, filters, location.search, userRole]);

  // ============================================================
  // DATA INTERFACE PARSING FOR NATIVE DATATABLE ROWS
  // ============================================================
  const uniqueYearsOptions = useMemo(() => {
    const yearsSet = new Set();
    filingList.forEach((f) => {
      [f?.tax_situation?.details?.PTF, f?.tax_situation?.details?.BTF].forEach(
        (type) => {
          if (Array.isArray(type?.situation?.taxYears)) {
            type.situation.taxYears.forEach((y) => yearsSet.add(String(y)));
          }
        },
      );
    });
    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [filingList]);

  const uniqueTypesOptions = useMemo(() => {
    const typesSet = new Set();
    filingList.forEach((f) => {
      (f?.tax_service?.services || []).forEach((s) => {
        const maps = {
          PTF: "Personal Tax Filing",
          BTF: "Business Tax Filing",
          TPC: "Tax Planning/Consulting",
        };
        typesSet.add(maps[s] || s);
      });
    });
    return Array.from(typesSet).sort();
  }, [filingList]);

  const headers = [
    {
      label: userRole === "CPA" ? "Taxpayer" : "Filing Type",
      key: "merchant",
      sortable: true,
      width: "240px",
    },
    { label: "Tax Year(s)", key: "tax_years", sortable: true, width: "140px" },
    {
      label: "Created On",
      key: "date",
      sortable: true,
      type: "tag",
      width: "140px",
    },
    {
      label: "Status",
      key: "status_label",
      sortable: true,
      type: "filing_status",
      width: "140px",
    },
    { label: "Amount", key: "amount", sortable: true, width: "140px" },
  ];

  const processedTableRows = useMemo(() => {
    return filteredFilingList.map((filing) => {
      const rawPrice =
        userRole === "CPA"
          ? filing.amount_paid_cpa
          : filing.amount_charged_user;
      const displayPrice =
        filing.status === "DRA" ? "N/A" : rawPrice ? `$${rawPrice}` : "N/A";
      const taxpayerLabel =
        userRole === "CPA"
          ? getTaxpayerName(filing)
          : formatFilingType(filing?.tax_service?.services);
      const stringDate = formatDate(filing.date_started);
      const statusText = formatStatus(filing.status);

      return {
        ...filing,
        merchant: taxpayerLabel,
        tax_years: formatFilingYears(filing),
        date: stringDate,
        status_label: statusText,
        amount: displayPrice,

        // Raw mathematical sorting handles
        merchant_value: taxpayerLabel.toLowerCase(),
        tax_years_value: formatFilingYears(filing),
        date_value:
          typeof filing.date_started === "number"
            ? filing.date_started
            : new Date(filing.date_started).getTime() / 1000,
        status_label_value: statusText.toLowerCase(),
        amount_value: filing.status === "DRA" ? -1 : Number(rawPrice) || 0,
      };
    });
  }, [filteredFilingList, userRole]);

  // ============================================================
  // THREE-DOT DROP MENU ACTIONS FACTORY
  // ============================================================
  const getFilingActions = (row) => {
    const actions = [];
    const status = row.status;
    const fId = row.filing_uuid;

    if (status === "DRA") {
      actions.push({
        label: "Edit Filing",
        onClick: () => navigate(`/tax-filing/new-filing/${fId}`),
      });
      actions.push({
        label: "Delete Draft",
        onClick: () => setDeleteConfirmationId(fId),
      });
    } else if (status === "ACT") {
      if (userRole === "CPA") {
        actions.push({
          label: "Review Details",
          onClick: () => handleCheckFiling(row.id),
        });
        actions.push({
          label: "Accept Job",
          onClick: () => handleAcceptFiling(row),
        });
      } else {
        actions.push({
          label: "View Status",
          onClick: () => navigate(`/tax-filing/filing/${fId}`),
        });
      }
    } else if (status === "CAN") {
      actions.push({
        label: "Remove Listing",
        onClick: () => setSoftDeleteConfirmationId(fId),
      });
    } else {
      actions.push({
        label: "View Details",
        onClick: () => navigate(`/tax-filing/filing/${fId}`),
      });
    }
    return actions;
  };

  // ============================================================
  // SEARCHFIELD ELEMENT JSX SPECIFICATION
  // ============================================================
  const searchRightElement = (
    <div
      className="search-input-element"
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <div className="form-field" style={{ flex: 1 }}>
        <input
          className="form-input"
          type="text"
          placeholder={
            userRole === "CPA" ? "Search by Taxpayer" : "Search by Filing Type"
          }
          value={merchantSearch}
          onChange={(e) => setMerchantSearch(e.target.value)}
          style={{ paddingRight: "40px" }}
        />
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/images/icons/search_blue.svg`}
        alt=""
        style={{
          position: "absolute",
          right: "10px",
          width: "22px",
          pointerEvents: "none",
        }}
      />
    </div>
  );

  // Core Content Layout Switcher
  let primaryDashboardContent;

  if (isCpaWithInactiveStatus) {
    primaryDashboardContent = loading ? (
      <LoadingPlaceholder rows={1} type="card-large" />
    ) : (
      <WelcomeCard
        message="You're almost there — let's finish your setup so your professional dashboard is ready."
        primaryCta={{
          text: "Finish onboarding",
          link: "/onboard",
          type: "link",
        }}
      />
    );
  } else {
    primaryDashboardContent = (
      <LoadingToRealTransitionWrapper
        isLoading={loading}
        ghost={
          <GhostCardContainer>
            <GhostDataTable
              headers={headers}
              rows={4}
              title={true}
              description={false}
              showSearch={true}
            />
          </GhostCardContainer>
        }
      >
        <DataTable
          title="My Filings"
          headers={headers}
          rows={processedTableRows}
          getActionMenu={getFilingActions}
          rightElement={searchRightElement}
          filterElement={true}
          onFilterClick={() => {
            setDraftFilters(filters);
            setIsFilterOpen(true);
          }}
          searchQuery={merchantSearch}
          searchHeaders={["merchant"]}
          emptyStateTitle={
            location.search.includes("segment=cancelled")
              ? "No Cancelled Filings"
              : userRole === "CPA"
              ? location.search.includes("segment=jobs")
                ? "No Filings Available"
                : "No Filings Yet"
              : "No filings yet"
          }
          emptyStateText={
            location.search.includes("segment=cancelled")
              ? "There are no cancelled filings to display."
              : userRole === "CPA"
              ? location.search.includes("segment=jobs")
                ? "New filings will appear here when taxpayers are matched with you."
                : "Filings you accept will appear here."
              : "You haven't started a filing. Select New Filing to begin."
          }
        />
      </LoadingToRealTransitionWrapper>
    );
  }

  // CPA Acceptance Popup Arrays Mapper
  const dynamicCpaOverlays = processedTableRows.flatMap((filing) => {
    const acceptState = acceptFilingStates[filing.filing_uuid] || {
      accepted: false,
      response: null,
    };
    const isCheckOpen = checkFilingStates[filing.id] || false;
    const items = [];

    if (acceptState.accepted && acceptState.response === 200) {
      items.push(
        <ModalNew key={`acc-${filing.id}`}>
          <CpaAccepted filingItem={filing} />
        </ModalNew>,
      );
    }
    if (acceptState.accepted && acceptState.response === 401) {
      items.push(
        <ModalNew key={`fail-${filing.id}`}>
          <CpaFailedAccept
            resetAcceptFiling={() =>
              setAcceptFilingStates((p) => {
                const n = { ...p };
                delete n[filing.filing_uuid];
                return n;
              })
            }
          />
        </ModalNew>,
      );
    }
    if (isCheckOpen) {
      items.push(
        <ModalNew key={`chk-${filing.id}`}>
          <CpaCheckFiling
            filingItem={filing}
            acceptFilingHandler={(e) => handleAcceptFiling(filing, e)}
            checkFilingHandler={(e) => handleCheckFiling(filing.id, e)}
          />
        </ModalNew>,
      );
    }
    return items;
  });

  return (
    <Fragment>
      {primaryDashboardContent}

      {/* Hard Draft Deletion Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirmationId}
        onClose={() => setDeleteConfirmationId(null)}
        onConfirm={handleDeleteFiling}
        title="Delete Filing"
        message="Are you sure you want to delete this filing? This action cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      {/* Soft Listing Removal Modal */}
      <ConfirmationModal
        isOpen={!!softDeleteConfirmationId}
        onClose={() => setSoftDeleteConfirmationId(null)}
        onConfirm={handleSoftDeleteFiling}
        title="Remove Filing"
        message="Are you sure you want to remove this filing from your list? This action cannot be undone."
        confirmText={isSoftDeleting ? "Removing..." : "Remove"}
        cancelText="Cancel"
        isLoading={isSoftDeleting}
      />

      {/* Filter Modal Dropdowns Canvas */}
      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={() => {
          setFilters(draftFilters);
          setIsFilterOpen(false);
        }}
        title={filterContent.filings.title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.filingYear}
            onChange={(e) =>
              setDraftFilters((prev) => ({
                ...prev,
                filingYear: e.target.value,
              }))
            }
          >
            <option value="">{filterContent.all_years}</option>
            {uniqueYearsOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.filingType}
            onChange={(e) =>
              setDraftFilters((prev) => ({
                ...prev,
                filingType: e.target.value,
              }))
            }
          >
            <option value="">{filterContent.all_types}</option>
            {uniqueTypesOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.status}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="">{filterContent.all_statuses}</option>
            {filingStatuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </FilterModal>

      {/* Injected CPA Event Popups Stack Array */}
      {dynamicCpaOverlays}

      {/* Job Already Taken Overlay */}
      {userRole === "CPA" && filingTaken && !loading ? (
        <ModalNew className="content-modal" style={{ maxWidth: "520px" }}>
          <PopupHeader
            title="Filing Already Taken"
            variant="compact"
            hideCloseButton="true"
          />
          <PopupContent>
            <PopupSection>
              This filing is currently being handled by another tax expert.
            </PopupSection>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                marginTop: "12px",
              }}
            >
              <button
                className="cta-button primary short"
                onClick={() => {
                  setFilingTaken(false);
                  navigate("/tax-filing?segment=jobs");
                }}
              >
                My Jobs
              </button>
              <button
                className="cta-button secondary short"
                onClick={() => {
                  setFilingTaken(false);
                  navigate("/");
                }}
              >
                Home
              </button>
            </div>
          </PopupContent>
        </ModalNew>
      ) : null}
    </Fragment>
  );
}

export default FilingListV2;
